"use client";
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useParams } from "react-router-dom";
import { ProductContext } from "../context/ProductContext";
import { useReviews } from "../context/ReviewContext";
import { Link } from "react-router-dom";
import { CDN_URL } from "../config/apiconfig";
import ProductDetails from "./ProductDetails";
import ReviewCard from "./ReviewCard";
import ProductCard from "./ProductCard";
import StarRating from "./StarRating";
import ProductCarousel from "./ProductCarousel";
import Features from "./Features";
import ProductDescription from "./ProductDescription";
import ProductNotFound from "./ProductNotFound";

function ProductListing() {
  const { id } = useParams();

  const {
    product,
    products,
    loading,
    productError,
    fetchProductById,
    averageRating: contextAverageRating,
    totalReviews: contextTotalReviews,
    reviewsLoading
  } = useContext(ProductContext);

  const [reviewsPerView, setReviewsPerView] = useState(3);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [uniformHeight, setUniformHeight] = useState(0);
  const cardRefs = useRef([]);

  useEffect(() => {
    if (id) fetchProductById(id);
  }, [id, fetchProductById]);

  const relatedProducts = Array.isArray(products)
    ? products.filter((p) => String(p.id) !== String(id)).slice(0, 6)
    : [];

  const [location, setLocation] = useState(() => ({
    query: {},
    path: "",
  }));

  const [deviceSize, setDeviceSize] = useState(() => "large");
  const { reviews: allReviews, loading: reviewsLoadingFromContext } = useReviews();
  const productReviews = Array.isArray(allReviews)
    ? allReviews.filter(
        (r) => String(r.productId) === String(id) && r.status === "Active",
      )
    : [];

  const mappedReviews = productReviews.map((r) => ({
    id: r.userId,
    name: `${r.userName}`,
    avatar: r.image
      ? `${CDN_URL}/${r.image}`
      : "https://cdn.acsdev.in/ManaPrint/1773641688_download%20(1).jpg",
    rating: Number.isFinite(r.rating) ? r.rating : 0,
    review: r.text,
  }));

  // Update reviews per view based on screen size
  useEffect(() => {
    const updateReviewsPerView = () => {
      const width = window.innerWidth;

      if (width < 640) {
        // Below sm breakpoint: 1 review
        setReviewsPerView(1);
      } else if (width < 768) {
        // sm breakpoint (640px-767px): 2 reviews
        setReviewsPerView(2);
      } else if (width < 1024) {
        // md breakpoint (768px-1023px): 2 reviews
        setReviewsPerView(2);
      } else {
        // lg and above (1024px+): 3 reviews
        setReviewsPerView(3);
      }
    };

    updateReviewsPerView();
    window.addEventListener("resize", updateReviewsPerView);
    return () => window.removeEventListener("resize", updateReviewsPerView);
  }, []);

  // Calculate max slides
  const maxSlides = Math.max(0, mappedReviews.length - reviewsPerView);
  const needsNavigation = mappedReviews.length > reviewsPerView;

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => Math.min(prev + 1, maxSlides));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  };

  // Calculate the transform offset for sliding animation
  const getTransformOffset = () => {
    const cardWidth = 100 / reviewsPerView; // Percentage width per card
    return -(currentSlide * cardWidth);
  };

  // Get responsive card width class based on reviews per view
  const getCardWidthClass = () => {
    switch (reviewsPerView) {
      case 1:
        return "w-full flex-shrink-0"; // Mobile: full width
      case 2:
        return "w-1/2 flex-shrink-0 px-2"; // Tablet: half width
      case 3:
      default:
        return "w-1/3 flex-shrink-0 px-2"; // Desktop: third width
    }
  };

  // Use the same rating logic as ProductDetails
  let displayRating = 0;
  let displayRatingCount = 0;
  let showRatings = true;

  console.log("ProductListing Rating Debug Info:", {
    reviewsLoading,
    contextAverageRating,
    contextTotalReviews,
    productAverageRating: product?.averageRating,
    productRating: product?.rating,
    productRatingCount: product?.ratingCount
  });

  if (reviewsLoading) {
    // Show loading state for ratings
    displayRating = 0;
    displayRatingCount = 0;
    showRatings = false;
  } else {
    // Priority 1: Use context rating data (from reviews API)
    const contextRating = Number(contextAverageRating);
    const contextCount = Number(contextTotalReviews);

    // Priority 2: Use product's average_rating from API
    const productApiRating = Number(product?.averageRating);

    // Priority 3: Use legacy product rating data
    const legacyRating = Number(product?.rating || 0);
    const legacyCount = Number(product?.ratingCount || 0);

    if (!isNaN(contextRating) && contextCount > 0) {
      // Use context data when available and valid
      displayRating = Math.max(0, Math.min(5, contextRating));
      displayRatingCount = contextCount;
    } else if (!isNaN(productApiRating)) {
      // Use product API rating (even if 0.0)
      displayRating = Math.max(0, Math.min(5, productApiRating));
      displayRatingCount = 0; // API doesn't provide count
    } else if (!isNaN(legacyRating) && (legacyRating > 0 || legacyCount > 0)) {
      // Use legacy data only if there's actual rating data
      displayRating = Math.max(0, Math.min(5, legacyRating));
      displayRatingCount = legacyCount;
    } else {
      // No rating data available
      displayRating = 0;
      displayRatingCount = 0;
    }
  }

  console.log("ProductListing Final Rating Display:", {
    displayRating,
    displayRatingCount,
    showRatings
  });

  // Calculate uniform height for cards
  const calculateUniformHeight = useCallback(() => {
    if (cardRefs.current.length === 0) return;

    // Wait for next tick to ensure DOM is updated
    setTimeout(() => {
      const heights = cardRefs.current
        .filter((ref) => ref && ref.offsetHeight > 0)
        .map((ref) => ref.offsetHeight);

      if (heights.length > 0) {
        const maxHeight = Math.max(...heights);
        setUniformHeight(maxHeight);
      }
    }, 100);
  }, []);

  // Recalculate heights when slide changes or reviews change
  useEffect(() => {
    calculateUniformHeight();
  }, [currentSlide, mappedReviews.length, calculateUniformHeight]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      calculateUniformHeight();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateUniformHeight]);

  return (
    <div className="overflow-hidden bg-orange-50 bg-opacity-90  max-md:pb-24">
      <main className="flex flex-col px-5 mt-5 w-full max-md:max-w-full">
        <div className="relative">
          {loading ? (
            <p className="text-center py-10">Loading product...</p>
          ) : productError === "Product not found" ? (
            <ProductNotFound />
          ) : (
            product && <ProductDetails product={product} />
          )}
        </div>

        {/* Only show product description and features if product exists and no error */}
        {!loading && !productError && product && (
          <>
            <section className="max-w-7xl w-full mx-auto py-2 md:py-3">
              <div className=" mx-auto px-0">
                <ProductDescription product={product} />
              </div>
            </section>
            <section className="max-w-7xl w-full mx-auto py-1 md:py-2">
              <div className=" mx-auto px-0">
                <Features product={product} />
              </div>
            </section>
          </>
        )}

        {/* Only show reviews and related sections if product exists and no error */}
        {!loading && !productError && product && (
          <>
            {/* Unified Review Section - Only show when there are reviews */}
            {!reviewsLoading && mappedReviews.length > 0 && (
              <section
                className="flex flex-col items-center self-center px-4 sm:px-8 md:px-16 pt-2.5 pb-3 mt-3 w-full max-w-[1288px] max-md:px-5 max-md:mt-4 max-md:max-w-full"
                aria-label="Customer reviews"
              >
                {/* Review Title */}
                <h1 className="text-4xl md:text-5xl lg:text-5xl tracking-wide leading-snug text-neutral-700 font-bold text-center max-md:max-w-full mb-3">
                  Review
                </h1>

                {/* Simplified Rating Summary */}
                <div className="flex flex-col items-center mb-8 w-full">
                  <div className="flex flex-col items-center space-y-3">
                    {/* Creative Star Display with Variable Sizes and Proper Partial Filling */}
                    <div className="flex items-end justify-center space-x-1">
                      {[1, 2, 3, 4, 5].map((starIndex) => {
                        // Fixed logic for proper partial filling
                        const isFilled = starIndex <= Math.floor(displayRating);
                        const isPartiallyFilled =
                          starIndex === Math.floor(displayRating) + 1 &&
                          displayRating % 1 !== 0;

                        // Calculate partial fill percentage
                        const partialFillPercentage = isPartiallyFilled
                          ? (displayRating % 1) * 100
                          : 0;

                        // Variable sizes: edges smaller, 2nd/4th medium, center largest
                        let size;
                        if (starIndex === 1 || starIndex === 5)
                          size = 32; // Edge stars - smallest
                        else if (starIndex === 2 || starIndex === 4)
                          size = 40; // 2nd and 4th - medium
                        else size = 48; // Center star - largest

                        const uniqueId = `star-gradient-${starIndex}-${Date.now()}`;

                        return (
                          <div
                            key={starIndex}
                            className="relative transform transition-all duration-300 hover:scale-110"
                          >
                            <svg
                              width={size}
                              height={size}
                              viewBox="0 0 24 24"
                              className="drop-shadow-lg"
                            >
                              <defs>
                                <linearGradient
                                  id={`goldGradient-${starIndex}`}
                                  x1="0%"
                                  y1="0%"
                                  x2="100%"
                                  y2="100%"
                                >
                                  <stop offset="0%" stopColor="#FFD700" />
                                  <stop offset="50%" stopColor="#FFA500" />
                                  <stop offset="100%" stopColor="#FF8C00" />
                                </linearGradient>

                                {/* Gradient for partial filling */}
                                {isPartiallyFilled && (
                                  <linearGradient
                                    id={uniqueId}
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="0%"
                                  >
                                    <stop
                                      offset={`${partialFillPercentage}%`}
                                      stopColor="#FFD700"
                                    />
                                    <stop
                                      offset={`${partialFillPercentage}%`}
                                      stopColor="#E5E7EB"
                                    />
                                  </linearGradient>
                                )}

                                <filter id={`glow-${starIndex}`}>
                                  <feGaussianBlur
                                    stdDeviation="2"
                                    result="coloredBlur"
                                  />
                                  <feMerge>
                                    <feMergeNode in="coloredBlur" />
                                    <feMergeNode in="SourceGraphic" />
                                  </feMerge>
                                </filter>
                              </defs>

                              <path
                                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                                fill={
                                  isFilled
                                    ? `url(#goldGradient-${starIndex})`
                                    : isPartiallyFilled
                                    ? `url(#${uniqueId})`
                                    : "#E5E7EB"
                                }
                                stroke={
                                  isFilled || isPartiallyFilled
                                    ? "#B45309"
                                    : "#9CA3AF"
                                }
                                strokeWidth="1"
                                filter={
                                  isFilled || isPartiallyFilled
                                    ? `url(#glow-${starIndex})`
                                    : "none"
                                }
                                className={`transition-all duration-500 ${isFilled || isPartiallyFilled ? "animate-pulse" : ""}`}
                              />
                            </svg>
                          </div>
                        );
                      })}
                    </div>

                    {/* Numeric Average Rating */}
                    <p className="text-2xl font-bold text-gray-800">
                      {displayRating === 0 ? "0" : displayRating.toFixed(1)}{" "}
                      <span className="text-sm font-medium text-gray-500">
                        / 5
                      </span>
                    </p>

                    {/* Customer Count */}
                    <p className="text-sm font-medium text-gray-600">
                      Loved by{" "}
                      <span className="font-bold text-amber-700">
                        {displayRatingCount > 0 ? displayRatingCount : mappedReviews.length}
                      </span>{" "}
                      happy customer{(displayRatingCount > 0 ? displayRatingCount : mappedReviews.length) !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>

                {/* Reviews Carousel */}
                <div
                  className="w-full max-w-7xl mx-auto overflow-hidden"
                  aria-label="Customer reviews carousel"
                >
                  <div className="relative mt-6 w-full">
                    <div className="flex items-center px-2 gap-2 sm:gap-3 md:gap-4">
                      {/* Navigation Arrow Left - Only show if navigation is needed */}
                      {needsNavigation && (
                        <button
                          onClick={prevSlide}
                          disabled={currentSlide === 0}
                          className="flex justify-center items-center w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg border-2 border-gray-200 text-gray-600 hover:bg-gray-50 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0 z-10"
                          aria-label="Previous reviews"
                        >
                          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                        </button>
                      )}

                      {/* Reviews Container */}
                      <div className="flex-1 overflow-hidden min-h-[240px] sm:min-h-[290px] md:min-h-[330px] lg:min-h-[370px]">
                        <div className="relative w-full h-full">
                          {/* Sliding Container */}
                          <div
                            className="flex transition-transform duration-500 ease-in-out w-full h-full"
                            style={{
                              transform: `translateX(${getTransformOffset()}%)`,
                            }}
                          >
                            {mappedReviews.map((review, index) => (
                              <div
                                key={`${review.name}-${index}`}
                                className={`${getCardWidthClass()}`}
                              >
                                <article className="flex flex-col items-center transition-transform duration-300 w-full max-w-xs mx-auto">
                                  <ReviewCard
                                    name={review.name}
                                    avatar={review.avatar}
                                    rating={review.rating}
                                    review={review.review}
                                    uniformHeight={uniformHeight}
                                  />
                                </article>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Navigation Arrow Right - Only show if navigation is needed */}
                      {needsNavigation && (
                        <button
                          onClick={nextSlide}
                          disabled={currentSlide >= maxSlides}
                          className="flex justify-center items-center w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg border-2 border-gray-200 text-gray-600 hover:bg-gray-50 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0 z-10"
                          aria-label="Next reviews"
                        >
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                        </button>
                      )}
                    </div>

                    {/* Carousel Indicators - Only show if navigation is needed */}
                    {mappedReviews.length > 0 &&
                      needsNavigation &&
                      maxSlides > 0 && (
                        <div
                          className="flex justify-center py-2 mt-2 sm:mt-3 space-x-2"
                          role="tablist"
                          aria-label="Carousel navigation"
                        >
                          {Array.from({ length: maxSlides + 1 }).map(
                            (_, index) => (
                              <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                  index === currentSlide
                                    ? "bg-teal-600 scale-125"
                                    : "bg-gray-300 hover:bg-gray-400"
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                                role="tab"
                                aria-selected={index === currentSlide}
                              />
                            ),
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </section>
            )}

            {/*
            Commented out for manual restoration if needed:
            Empty State for No Reviews:
            {!reviewsLoading && mappedReviews.length === 0 && (
              <div className="mt-3 mb-6 px-2 sm:px-4 max-w-7xl mx-auto w-full">
                <p className="text-center py-2 text-gray-500">
                  No reviews for this product yet
                </p>
              </div>
            )}
            */}

            <section className="pt-2 pb-6">
              <div className="max-w-[1400px] mx-auto px-6">
                <ProductCarousel
                  title="You may also like"
                  subtitle="Transform your space with our curated collection"
                  products={relatedProducts.length ? relatedProducts : []}
                />
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default ProductListing;
